// =============================================================================
// Release notes
//
// Plain-language "What's New" entries shown in the WhatsNewDialog that
// opens from the version number in the footer. This is the
// staff-friendly counterpart to the root `CHANGELOG.md`: the changelog
// is precise and technical (commit-level, for developers/operators),
// while this list rewrites each release into one or two non-technical
// lines a regular portal user can understand.
//
// When you ship a release:
//   1. Bump `apps/web/package.json#version`.
//   2. Add a `## [x.y.z]` section to `CHANGELOG.md` (the full story).
//   3. Add a matching entry at the TOP of `RELEASE_NOTES` below, with a
//      short, jargon-free summary — no commit hashes, no internal /
//      security / back-end-only detail. Keep purely behind-the-scenes
//      releases out, or fold them into a generic "Behind-the-scenes
//      improvements and fixes" line.
//
// The newest entry's version should equal the running `__APP_VERSION__`
// so the dialog can tag it as "Current".
// =============================================================================

export interface ReleaseNote {
  /** Semver, matching the `CHANGELOG.md` heading and package.json. */
  version: string;
  /** Ship date as `YYYY-MM-DD` (rendered via formatWallClockDate). */
  date: string;
  /**
   * The plain-language summary. A single string renders as one line; an
   * array renders as a short bullet list (keep it to a couple of tiny
   * bullets — this is a glance, not a manual).
   */
  summary: string | string[];
}

// Newest first — the dialog renders this order top-to-bottom and tags
// the entry matching the running build as "Current".
export const RELEASE_NOTES: ReleaseNote[] = [
  {
    version: "1.25.0",
    date: "2026-09-12",
    summary: [
      "A fresh, cleaner look across the whole portal: signing in, the trip request form, Booked trips, your account and every pop-up, in both light and dark themes.",
      "New: press Ctrl+K (⌘K on a Mac), or click Search at the top, to jump to any page or find one of today's trips.",
      "The trip request form now shows your trip taking shape beside the form as you fill it in.",
    ],
  },
  {
    version: "1.24.1",
    date: "2026-09-04",
    summary:
      "Fixed the Off-site trips filter listing trips that do go to a Mount Sinai building. Addresses written with the building, floor and suite after the street - like \"4300 Alton Rd Gumenick Bldg 2nd Fl Ste 2522\" - are now recognised as the campus.",
  },
  {
    version: "1.24.0",
    date: "2026-09-04",
    summary:
      "Admin > Bookings has a new \"Off-site trips\" filter for reviewing trips where neither the pickup nor the drop-off is one of your locations - a trip that never touches the hospital. Normal patient transport (hospital to home, or home to hospital) is not included, so the list stays short enough to actually review.",
  },
  {
    version: "1.23.2",
    date: "2026-09-02",
    summary:
      "If you move a trip to a pickup time dispatch considers too soon, the portal now says so and keeps your changes on screen so you can pick a later time, rather than showing a generic failure.",
  },
  {
    version: "1.23.1",
    date: "2026-09-02",
    summary:
      "If dispatch won't accept a trip change because the pickup is too close, the portal now says so and tells you to call dispatch. It used to report \"the dispatch service is unavailable\", which looked like an outage and left people retrying a change that could not go through.",
  },
  {
    version: "1.23.0",
    date: "2026-09-02",
    summary:
      "Correcting a member's contact phone now also works when the old number looked valid - a patient who changed carriers, or who gave a caregiver's number instead of their own. It replaces that number on the trips that carry it, and leaves trips recorded with a genuinely different contact untouched.",
  },
  {
    version: "1.22.2",
    date: "2026-09-02",
    summary:
      "Updating a member's contact phone now tells you clearly when the change could not be applied, instead of showing a success message. If that happens, the member has no saved profile and no trip with a malformed number, so Royal needs to correct it in TripLink.",
  },
  {
    version: "1.22.1",
    date: "2026-09-02",
    summary:
      "Clearer wording when you update a member's contact phone, so it says exactly which trips were changed. Fixes a confusing message that could report zero trips updated after a save that worked.",
  },
  {
    version: "1.22.0",
    date: "2026-09-02",
    summary: [
      "Merging two member profiles now sticks. Previously a merge looked like it worked and then the old profile came back a minute or two later, because the overnight sync with dispatch kept restoring the original MRN. Your correction now wins.",
      "You can now fix a member's contact phone. Open a member in Members and edit the Contact phone field - it repairs any of their trips whose stored number is malformed. Numbers that arrived from dispatch with an extra country code have already been cleaned up automatically.",
    ],
  },
  {
    version: "1.21.43",
    date: "2026-08-20",
    summary:
      "The form now warns you if the patient already has a trip booked on the day you're booking, and shows which trip it is and who booked it. With several departments booking into the same portal, this catches the case where someone else has already arranged transport. You can still go ahead if it really is a second, separate trip.",
  },
  {
    version: "1.21.42",
    date: "2026-08-18",
    summary:
      "You can now change a trip's time or addresses up to 6 hours before pickup. It used to be 24 hours, which meant the Edit button never appeared on same-day or next-morning trips and you had to cancel and rebook instead. Cancelling is unchanged and has never had a time limit.",
  },
  {
    version: "1.21.41",
    date: "2026-08-14",
    summary: [
      'The Members export now flags every profile that needs a look. Some profiles were included in the "Review needed" download while the file itself said they were fine and gave no reason — usually a name with unusual capitalization, a number in it, or a word repeated twice.',
      "Behind-the-scenes improvements to the admin tools' safety checks.",
    ],
  },
  {
    version: "1.21.40",
    date: "2026-08-05",
    summary:
      "Fixed an error that could appear when signing in shortly after an update was released, before the browser had picked up the new version.",
  },
  {
    version: "1.21.39",
    date: "2026-08-05",
    summary: [
      "Trips now need at least 30 minutes' notice (45 when you give an appointment time, since dispatch works backwards from it). The form tells you the earliest time it can take.",
      "Will Call is unaffected, and admins can still book at short notice by giving a brief reason.",
    ],
  },
  {
    version: "1.21.38",
    date: "2026-08-05",
    summary:
      "Invoices now show what an extra charge was for. A trip with a hand-set fare lists the contract rate, the amount added, and the reason, so a reviewer can see at a glance why it differs from the usual price.",
  },
  {
    version: "1.21.37",
    date: "2026-07-30",
    summary:
      "If a booking can't be routed between the pickup and drop-off, the form now says so and tells you to re-check the address, instead of showing a generic error that made it look like the system was down.",
  },
  {
    version: "1.21.36",
    date: "2026-07-29",
    summary:
      "Reports: the Print / PDF button now produces a proper document - charts keep their colors on paper, cards no longer split across pages, and the report uses the full page width.",
  },
  {
    version: "1.21.35",
    date: "2026-07-14",
    summary: "Behind-the-scenes fixes to the dispatch sync.",
  },
  {
    version: "1.21.34",
    date: "2026-07-14",
    summary: "Behind-the-scenes database performance and accuracy improvements.",
  },
  {
    version: "1.21.33",
    date: "2026-07-07",
    summary:
      "The same-address warning now also appears when editing a trip, not just when booking one, so a missing residence gets caught either way.",
  },
  {
    version: "1.21.32",
    date: "2026-07-07",
    summary:
      "Fixed a misleading label: a future trip that just isn't priced yet now reads \"Not priced yet\" instead of \"trip was deleted upstream.\"",
  },
  {
    version: "1.21.31",
    date: "2026-07-07",
    summary:
      "Time fields are more forgiving: type \"900\" and it becomes \"9:00\" automatically, no need to add the colon yourself.",
  },
  {
    version: "1.21.30",
    date: "2026-07-07",
    summary:
      "Editing a trip's time or address now shows the new value in the portal right away. Before, the change saved to dispatch but the booking list kept showing the old time, so edits looked like they didn't stick.",
  },
  {
    version: "1.21.29",
    date: "2026-07-01",
    summary:
      "Admins: fixed an error that stopped a full-month invoice from generating. Invoices of any size now generate reliably.",
  },
  {
    version: "1.21.28",
    date: "2026-07-01",
    summary: [
      "The booking form now warns if the pickup and drop-off are the same address, so it's easy to catch when a residence was left out by mistake.",
      "If a trip's pickup or drop-off is corrected on the dispatch side after booking, the portal now updates to match instead of showing the old address.",
    ],
  },
  {
    version: "1.21.27",
    date: "2026-06-29",
    summary: "Behind-the-scenes cleanup and changelog fixes.",
  },
  {
    version: "1.21.26",
    date: "2026-06-29",
    summary:
      "Admins: a new Fare overrides tab on the Billing page lists every trip with a hand-set fare for the selected period, including the reason, who set it, and when.",
  },
  {
    version: "1.21.25",
    date: "2026-06-27",
    summary:
      "Admins: the Audit log now hides routine system/sync events by default (one toggle shows them) and presents the rest in plain language - a clear 'System' actor and a readable description of exactly what changed.",
  },
  {
    version: "1.21.24",
    date: "2026-06-24",
    summary:
      "Booked Trips got a cleaner, more consistent look that matches the rest of the portal - a clearer header, a tidier layout, and trip cards that show trip type, transport, and status at a glance.",
  },
  {
    version: "1.21.23",
    date: "2026-06-24",
    summary: [
      "Edit a trip's date: the trip-edit screen now has a clear Date field, so you can move a booking to another day without cancelling and rebooking.",
      "Admins: Edit trip and Activate Will Call are now available right in the admin Bookings list, next to Cancel and Delete.",
    ],
  },
  {
    version: "1.21.22",
    date: "2026-06-23",
    summary: [
      "Booking: a friendly ambulance “booking in progress” animation now appears if a booking takes a moment — instant ones never flash it.",
      "Booking errors now show a clear, friendly message instead of technical wording.",
    ],
  },
  {
    version: "1.21.21",
    date: "2026-06-23",
    summary:
      "Reports: the hover lift + glow now applies to every card on the dashboard — the KPI row, spend, stat tiles, and charts — consistent across the whole page.",
  },
  {
    version: "1.21.20",
    date: "2026-06-23",
    summary: [
      "Reports: hover any bar in Busiest pickup hours to see its exact hour and pickup count.",
      "Reports: the analytics cards now gently glow and lift when you hover them.",
    ],
  },
  {
    version: "1.21.19",
    date: "2026-06-23",
    summary:
      "Reports → Busiest pickup hours: the phantom 11 PM spike from placeholder-timed (no-scheduled-time) trips is now actually gone.",
  },
  {
    version: "1.21.18",
    date: "2026-06-23",
    summary:
      "When a new version ships you’ll now see a clear pop-up in the middle of the screen — “Refresh now” to update, or “Later” to tuck it into the corner and refresh when you’re ready, so you never lose a booking in progress.",
  },
  {
    version: "1.21.17",
    date: "2026-06-23",
    summary: [
      "Reports → Busiest pickup hours no longer counts Will-Call trips (they have no real pickup time), so the misleading 11 PM spike is gone.",
      "Reports → MRN compliance chart now shows the daily compliance %, so the line matches the headline number instead of looking like it’s bouncing around.",
    ],
  },
  {
    version: "1.21.16",
    date: "2026-06-23",
    summary: "Behind-the-scenes improvements and fixes.",
  },
  {
    version: "1.21.15",
    date: "2026-06-23",
    summary: "Behind-the-scenes reliability fix for the update prompt.",
  },
  {
    version: "1.21.14",
    date: "2026-06-23",
    summary:
      "Fixed the “Update available — Refresh now” prompt so it reliably appears when a new version ships — previously it could stay hidden, leaving you on an old version with no nudge to refresh.",
  },
  {
    version: "1.21.13",
    date: "2026-06-23",
    summary:
      "Reports: the date range can no longer end up backwards — if you set the start after the end (or vice versa), the other date follows automatically, so the dates you pick always match the numbers shown.",
  },
  {
    version: "1.21.12",
    date: "2026-06-23",
    summary:
      "Reports: the “vs prior period” comparison now lines up by day of the week — a single day compares to the same day last week (Monday vs last Monday) instead of to the day before, so the percentages make sense. Comparisons for longer ranges (last 7/30 days, this/last month) are unchanged.",
  },
  {
    version: "1.21.11",
    date: "2026-06-22",
    summary:
      "Behind-the-scenes: the server now uses a dedicated key to look up map coordinates for hand-typed addresses (the reliable fix for the “dispatch service unavailable” errors), which takes effect once the new key is configured.",
  },
  {
    version: "1.21.10",
    date: "2026-06-22",
    summary:
      "Fixed booking failures for hand-typed addresses: the server can now reliably look up a typed address's map location, so trips entered without picking a Google suggestion go through instead of erroring with “dispatch service unavailable”.",
  },
  {
    version: "1.21.9",
    date: "2026-06-22",
    summary:
      "Added a safeguard so a mis-entered saved Mount Sinai location can't display a doubled address.",
  },
  {
    version: "1.21.8",
    date: "2026-06-22",
    summary:
      "Behind-the-scenes: strengthened the internal audit trail so a dropped record is always logged (and flagged for security-relevant actions) instead of silently lost.",
  },
  {
    version: "1.21.7",
    date: "2026-06-22",
    summary:
      "Behind-the-scenes reliability: the server now restarts cleanly after a rare database hiccup instead of dropping requests, and we're alerted if a scheduled report ever fails to reach its recipients.",
  },
  {
    version: "1.21.6",
    date: "2026-06-22",
    summary:
      "Consistency: the Edit Trip dialog now uses the exact same address experience as the booking form — pick from the dropdown, see the full address with a green “Location confirmed”, no separate city/ZIP box.",
  },
  {
    version: "1.21.5",
    date: "2026-06-22",
    summary:
      "Cleaner, consistent addresses: every confirmed address now shows in full on one line with a small green “Location confirmed” check — whether you pick it from Google or from the Mount Sinai locations list. The old second “Confirmed: city, ZIP” line is gone.",
  },
  {
    version: "1.21.4",
    date: "2026-06-22",
    summary:
      "Simpler address entry: pick the address from the Google dropdown and that's it — the separate “City, State ZIP” box is gone. If you type an address without choosing it from the dropdown, a gentle reminder appears so dispatch always gets the exact map location.",
  },
  {
    version: "1.21.3",
    date: "2026-06-22",
    summary:
      "When you pick an address from the dropdown, the apartment/suite number is now kept (e.g. “20505 E Country Club Dr APT 536”) instead of being dropped. Applies to both pick-up and drop-off.",
  },
  {
    version: "1.21.2",
    date: "2026-06-22",
    summary:
      "Fixed a bug where some trips — typically apartment/suite or hand-typed addresses — could fail to book with a “dispatch service unavailable” error. The portal now looks up the map coordinates for those addresses on the server before sending the trip to dispatch, so they go through.",
  },
  {
    version: "1.21.1",
    date: "2026-06-22",
    summary:
      "Behind-the-scenes reliability: expanded error monitoring so the team is alerted to background problems that used to be invisible — a stalled TripLink sync, an undelivered login or password-reset email, a trip that didn’t save, or a downstream sync hiccup. No patient information is ever included, and nothing changes for day-to-day use.",
  },
  {
    version: "1.21.0",
    date: "2026-06-17",
    summary:
      "Sessions: if your sign-in expires while a tab is left open, the portal now takes you cleanly back to the login screen with a short “your session expired” note — instead of showing “couldn’t load” errors. Sign back in and you return right where you were, with any in-progress trip form preserved.",
  },
  {
    version: "1.20.23",
    date: "2026-06-17",
    summary:
      "Accessibility improvements: screen readers now correctly read the labels and error messages on the booking form’s address fields, and confirmation pop-ups are properly announced as dialogs. No visual change for sighted users.",
  },
  {
    version: "1.20.22",
    date: "2026-06-17",
    summary:
      "Behind-the-scenes fix so the team’s diagnostic error reporting works end-to-end (no patient information is ever included). No change to day-to-day use.",
  },
  {
    version: "1.20.21",
    date: "2026-06-17",
    summary:
      "Behind-the-scenes reliability work: error reporting is now active so the team is alerted to glitches faster (no patient information is ever included), and a page waiting on a stalled connection now shows a clear “try again” message instead of spinning indefinitely. We also added a large suite of automated tests and stricter pre-release checks to catch problems before they reach you.",
  },
  {
    version: "1.20.20",
    date: "2026-06-17",
    summary:
      "Invoice notes now match the Booked Trips view exactly \u2014 a bug was trimming real instructions like \u201Creq w/c\u201D or \u201Creq O2\u201D down to a stray letter on the invoice. Also: an empty note coming from the sync no longer blanks a trip\u2019s caller name + note, the bookings spreadsheet stops showing a $1.50 fee on not-yet-priced trips, and generating an invoice now warns when some trips are still unpriced (TBD). Takes effect on the next invoice regenerate.",
  },
  {
    version: "1.20.19",
    date: "2026-06-17",
    summary:
      "The monthly report\u2019s downloadable spreadsheet now totals the same as the dashboard and the invoice. It was pricing a different set of trips (everything booked that month, instead of trips dated that month) and keeping fares on some cancelled trips; it now uses the exact same billing engine, so bookings, invoice, dashboard, and report all foot to the same number. No change to any rate \u2014 just the report\u2019s query.",
  },
  {
    version: "1.20.18",
    date: "2026-06-16",
    summary:
      "Invoice notes are a touch cleaner: when a phone number is removed, its leftover label (\u201CCall back:\u201D, \u201Cph#\u201D, \u201CHome:\u201D, \u2026) is dropped too, so a note never reads as just a stray label. Genuine instructions are kept (\u201Cin front of ER. ph#\u201D \u2192 \u201Cin front of ER\u201D). Cosmetic only \u2014 it applies to future invoices, nothing to re-send or re-sync.",
  },
  {
    version: "1.20.17",
    date: "2026-06-16",
    summary:
      "Invoice \u201CNotes\u201D are now cleaned for portal/form bookings too (not just TMS-synced ones), so leftover phone numbers and metadata no longer slip onto the client invoice. The cleaner also handles a couple of odd note shapes (a \u201CDeartament\u201D typo and slash/\u00B7-separated dumps). This is invoice-display only \u2014 stored booking notes are untouched \u2014 so just Regenerate the invoice (no re-sync needed).",
  },
  {
    version: "1.20.16",
    date: "2026-06-16",
    summary:
      "The Booked Trips page is cleaner: trips are now compact rows that expand on click to show the full detail (DOB, phones, addresses, cost center, note), matching the Admin bookings list instead of the big always-open cards. The Detailed/Compact toggle now opens or collapses the rows, and quick actions stay one tap away. Same trips, dates, search, and filters \u2014 just a tidier layout.",
  },
  {
    version: "1.20.15",
    date: "2026-06-16",
    summary:
      "The sync now re-cleans dispatcher notes + caller names on trips that already had them \u2014 previously it only filled in blank ones, so earlier note-cleanup fixes couldn\u2019t reach trips with old, messy notes. Form bookings are untouched. After deploy, run a backfill over the invoice\u2019s dates and regenerate to clear the leftover \u201C)\u201D and other residue.",
  },
  {
    version: "1.20.14",
    date: "2026-06-16",
    summary:
      "Two billing tidy-ups: the invoice \u201CNotes\u201D column no longer leaves stray brackets/punctuation (e.g. a lone \u201C)\u201D) when metadata is removed, and the read-only Invoice Review tab now adds a \u201CReviewer notes\u201D column that suggests a cost center / MRN found in the trip note for flagged trips (\u201Cverify and enter in TMS\u201D) \u2014 hints only, nothing is changed automatically. Note: takes effect after the next sync/backfill.",
  },
  {
    version: "1.20.13",
    date: "2026-06-16",
    summary:
      "The invoice \u201CNotes\u201D column is cleaner: leftover dispatch/portal metadata (Department, MRN, Cost Center, Requester Email) and phone numbers that slipped through on some trips are now removed, including from one-line notes where the labels ran together. Genuine pickup/drop-off instructions are kept. Takes effect on the next sync \u2014 already-synced trips need a re-sync/backfill to pick it up.",
  },
  {
    version: "1.20.12",
    date: "2026-06-16",
    summary:
      "New \u201CInvoice review\u201D tab on the Billing page: a read-only check that lists trips needing attention for a period \u2014 missing or odd-looking cost centers and MRNs, round-trip legs whose partner has an issue, and deadhead fare add-ons \u2014 with totals that match the invoice. It only flags things to fix by hand in TMS; the portal never changes any data, and there are no buttons to edit or apply anything.",
  },
  {
    version: "1.20.11",
    date: "2026-06-16",
    summary:
      "Clearer report wording: the monthly report now says \u201CN bookings created\u201D (all requests) instead of \u201CN trips,\u201D so it can\u2019t be mistaken for the smaller \u201Cbillable trips\u201D count shown in the spend section. The weekly / daily digests, the department summaries, the dashboard, and the CSV column headers all use the same consistent labels. Wording only \u2014 no number changed.",
  },
  {
    version: "1.20.10",
    date: "2026-06-16",
    summary:
      "Invoices now fill in the \u201CCaller\u2019s Name\u201D and \u201CNotes\u201D columns for trips booked through TripLink (previously blank) \u2014 the caller and the dispatcher\u2019s instructions are recovered from the trip note, with safeguards so a department name is never shown as a caller. No change to any dollar amount.",
  },
  {
    version: "1.20.9",
    date: "2026-06-16",
    summary:
      "The internal Billing page\u2019s Invoices table now fits cleanly with no horizontal scrollbar \u2014 each row\u2019s actions are tucked into a compact \u201C\u2026\u201D menu (download Excel / PDF, regenerate, mark sent / paid, void). No change to any data or amounts.",
  },
  {
    version: "1.20.8",
    date: "2026-06-16",
    summary:
      "Invoice polish: the itemized sheet\u2019s County / City / ZIP columns are now accurate (rebuilt from US Census data, with a city-based fallback when an address has no ZIP), the PDF embeds its fonts so it opens correctly in every viewer, and a not-yet-sent invoice can be \u201CRegenerated\u201D in place to pick up corrections while keeping its number. No dollar amount changed.",
  },
  {
    version: "1.20.7",
    date: "2026-06-16",
    summary:
      "The internal Billing page can now generate and track invoices: pick a period, generate an invoice (numbered MSMC-2026-003, 004, \u2026), download it as Excel or PDF, and mark it sent or paid (or void it). The totals match the Billing summary to the cent.",
  },
  {
    version: "1.20.6",
    date: "2026-06-16",
    summary:
      "Billing accuracy: day vs night fares now use a trip\u2019s real scheduled pickup time instead of the appointment time, so appointment-mode trips are no longer mis-priced. A trip with no pickup time yet is shown as \u201CTBD\u201D rather than guessed, and a new \u201CNo-fare\u201D filter on the Bookings page (with a count) makes those easy to find.",
  },
  {
    version: "1.20.5",
    date: "2026-06-16",
    summary:
      "The Royal team's internal TripLink Sync menu can now backfill a custom number of days (with 30/60/90 quick presets) instead of a fixed 60, making it easy to re-pull as much trip history as needed.",
  },
  {
    version: "1.20.4",
    date: "2026-06-15",
    summary:
      "Behind-the-scenes improvements: the portal now records each trip\u2019s real scheduled pickup time from TMS, groundwork for more accurate day/night fare pricing. No change to how trips are priced today.",
  },
  {
    version: "1.20.3",
    date: "2026-06-15",
    summary:
      "The Royal team's internal TripLink Sync menu now shows a live \u201cSync in progress\u2026\u201d indicator and the last sync's result, so it's clear when a sync is already running instead of a click seeming to do nothing.",
  },
  {
    version: "1.20.2",
    date: "2026-06-15",
    summary:
      "Billing accuracy: when a trip\u2019s service type is changed in TripLink after booking, the portal now keeps it in sync so the trip bills at the correct rate. The Royal team can also re-sync a single trip on demand from the TripLink Sync menu.",
  },
  {
    version: "1.20.1",
    date: "2026-06-15",
    summary:
      "Behind-the-scenes improvements: added privacy-safe error reporting so the team gets alerted to glitches faster \u2014 no patient information is ever included.",
  },
  {
    version: "1.20.0",
    date: "2026-06-15",
    summary:
      "Internal Billing page: review a period\u2019s spend plus standby-vehicle charges to match the TMS invoice (invoice/CSV generation coming soon).",
  },
  {
    version: "1.19.11",
    date: "2026-06-15",
    summary:
      "Billing: a finished or no-show trip that briefly passed through a cancellation is no longer counted as cancelled \u2014 a leftover cancellation flag no longer drops a completed trip from the billable count, and the sync now clears that flag once a trip is no longer cancelled.",
  },
  {
    version: "1.19.10",
    date: "2026-06-15",
    summary:
      "Reports & Bookings: a trip whose date falls inside a From/To range now always shows up in that range \u2014 fixed a couple of recently-synced June trips that were being left out of the date-range view, the spend window, and the export.",
  },
  {
    version: "1.19.9",
    date: "2026-06-15",
    summary:
      "Sync: a trip booked for an early-morning pickup is no longer filed a day early \u2014 its service date now matches the pickup time exactly, so it shows up in the reports and date-range views for the right day.",
  },
  {
    version: "1.19.8",
    date: "2026-06-15",
    summary:
      "Billing: no-show trips always price from their mileage (using the scheduled pickup time, or a day-rate default when none) instead of getting stuck as \u201CTBD.\u201D",
  },
  {
    version: "1.19.7",
    date: "2026-06-15",
    summary:
      "Sync: a trip\u2019s service date is now refreshed when it gains a pickup time after being booked Will-Call \u2014 fixes completed trips silently dropping out of the date-range filter, reports, and exports.",
  },
  {
    version: "1.19.6",
    date: "2026-06-15",
    summary:
      "Billing: completed Will-Call trips booked through the portal form now price off their real pickup time instead of staying stuck as \u201CTBD,\u201D matching trips booked directly in dispatch.",
  },
  {
    version: "1.19.5",
    date: "2026-06-15",
    summary:
      "Billing: fixed member/customer cancellations not billing the IT fee \u2014 the previous rule matched cancellation names that don\u2019t exist in the data; the spend tile\u2019s \u201CCancelled \u2014 IT fee only\u201D count and total are correct again.",
  },
  {
    version: "1.19.4",
    date: "2026-06-15",
    summary:
      "Billing: completed Will-Call trips now price correctly off their actual pickup time instead of getting stuck as \u201CTBD.\u201D",
  },
  {
    version: "1.19.3",
    date: "2026-06-15",
    summary:
      "Billing: only member cancellations incur the IT fee; all other cancellation types are now excluded, matching the invoice.",
  },
  {
    version: "1.19.2",
    date: "2026-06-15",
    summary:
      "Billing: broker/company (internal test) cancellations no longer incur an IT fee; only member cancellations are billed fee-only.",
  },
  {
    version: "1.19.1",
    date: "2026-06-15",
    summary:
      "Reports: the Transportation spend tile now leads with Total trips and breaks them out as Billable (full fare) / Cancelled (IT fee only) / Unpriced (TBD) — matching the official invoice — instead of an ambiguous \u201Cbilled\u201D count.",
  },
  {
    version: "1.19.0",
    date: "2026-06-13",
    summary:
      "Reports: admins can now choose what time the weekly and monthly reports are emailed (Eastern Time).",
  },
  {
    version: "1.18.4",
    date: "2026-06-13",
    summary:
      "Reports: hover, focus, or tap any day on the Daily Volume chart to see that day's exact booking count.",
  },
  {
    version: "1.18.3",
    date: "2026-06-13",
    summary:
      "Reports: the date bar no longer jumps to a second row when you select a date range.",
  },
  {
    version: "1.18.2",
    date: "2026-06-13",
    summary:
      "Removed the duplicate sync-freshness line from the account menu — the Reports dashboard's TMS Sync tile is the single source for sync health.",
  },
  {
    version: "1.18.1",
    date: "2026-06-13",
    summary:
      "Reports: the date-range presets and From/To pickers now sit on one tidy row instead of two.",
  },
  {
    version: "1.18.0",
    date: "2026-06-13",
    summary:
      "Moved the internal TripLink sync tools into the account menu to declutter the Bookings page — Export and Refresh stay right where they are.",
  },
  {
    version: "1.17.2",
    date: "2026-06-13",
    summary: "Behind-the-scenes refinements to the Bookings list.",
  },
  {
    version: "1.15.1",
    date: "2026-06-13",
    summary:
      "Tidied up the Bookings list so a booking no longer shows the same person’s name twice — the “Booked by” tag now appears only when it’s someone other than the requester.",
  },
  {
    version: "1.15.0",
    date: "2026-06-13",
    summary:
      "You can now click the version number at the bottom of the page to see this “What’s new” list of recent updates.",
  },
  {
    version: "1.14.0",
    date: "2026-06-13",
    summary:
      "A smoother, calmer loading experience — pages now ease in with the MSMC mark instead of a bare spinner, and the admin screens fill in more gently.",
  },
  {
    version: "1.13.0",
    date: "2026-06-12",
    summary:
      "Subtle, purposeful animations across the admin pages so it’s clearer what just changed when lists update or details open.",
  },
  {
    version: "1.12.0",
    date: "2026-06-12",
    summary: [
      "Easier to use on tablets: bigger tap targets and better keyboard support.",
      "Clearer trip-status labels (e.g. “En Route”, “Late Cancel”) and improved color contrast in light mode.",
    ],
  },
  {
    version: "1.11.0",
    date: "2026-06-12",
    summary: "Behind-the-scenes improvements to multi-leg trip handling.",
  },
  {
    version: "1.10.0",
    date: "2026-06-12",
    summary: [
      "Trip prices now match the signed Mount Sinai rate contract.",
      "Reports use New York time, so daily totals and “today” no longer drift in the evening. Rising spend now shows in red.",
    ],
  },
  {
    version: "1.9.0",
    date: "2026-06-12",
    summary:
      "“Revenue” is now called “Transportation spend” (what Mount Sinai pays), and the weekly and monthly report emails now include a spend summary.",
  },
  {
    version: "1.8.0",
    date: "2026-06-12",
    summary:
      "Added a spend section to the Reports dashboard — totals by service type and by day, for staff with pricing access.",
  },
  {
    version: "1.7.0",
    date: "2026-06-12",
    summary:
      "Each trip’s charges are now itemized — trip fare, IT fee, and grand total — on the booking card and in the CSV export.",
  },
  {
    version: "1.6.0",
    date: "2026-06-12",
    summary:
      "Bookings now show trip mileage and a calculated price from the Mount Sinai rate sheet. A new permission controls who can see pricing.",
  },
  {
    version: "1.5.0",
    date: "2026-06-12",
    summary:
      "New Permissions page lets super-admins control who can do what, and regular staff can now view Booked Trips to self-serve trip ETAs.",
  },
  {
    version: "1.4.0",
    date: "2026-05-29",
    summary:
      "Recurring trips: book the same trip across many dates at once (pick the weekdays and number of weeks), with each date as its own editable trip.",
  },
  {
    version: "1.1.0",
    date: "2026-05-26",
    summary:
      "Launched the Reports dashboard and automated weekly/monthly summary emails, plus one-click CSV exports for bookings, members, and the audit log.",
  },
  {
    version: "1.0.0",
    date: "2026-05-20",
    summary:
      "First official release — light & dark themes, the full admin toolset, and the live TripLink trip sync.",
  },
];
